import React from 'react';
import { formatDistanceToNow } from 'date-fns';

interface PaymentCardProps {
  payment: any;
  onViewDetails?: () => void;
  onDownloadReceipt?: () => void;
  compact?: boolean;
}

const PaymentCard: React.FC<PaymentCardProps> = ({
  payment,
  onViewDetails,
  onDownloadReceipt,
  compact = false
}) => {
  const formatCurrency = (amount: number) => `P${amount.toFixed(2)}`;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'refunded':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'pending':
        return '⏳';
      case 'failed':
        return '❌';
      case 'refunded':
        return '↩️';
      default:
        return '❓';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'cash_on_delivery':
        return '💰';
      case 'wallet':
        return '👛';
      case 'card':
        return '💳';
      case 'bank_transfer':
        return '🏦';
      default:
        return '💳';
    }
  };

  if (compact) {
    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${getStatusColor(payment.payment_status)}`}>
            {getStatusIcon(payment.payment_status)}
          </div>
          <div>
            <p className="font-medium text-gray-900">{payment.errand_title}</p>
            <p className="text-sm text-gray-500">
              {formatCurrency(payment.amount)} • {formatDistanceToNow(new Date(payment.transaction_date), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.payment_status)}`}>
            {payment.payment_status}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(payment.payment_status)}`}>
              {getStatusIcon(payment.payment_status)}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">{payment.errand_title}</h3>
              <p className="text-sm text-gray-600">{payment.errand_type} Service</p>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span>{formatDistanceToNow(new Date(payment.transaction_date), { addSuffix: true })}</span>
            <span>•</span>
            <span className="flex items-center space-x-1">
              <span>{getPaymentMethodIcon(payment.payment_method)}</span>
              <span>{payment.payment_method.replace('_', ' ')}</span>
            </span>
          </div>
        </div>
        <div className="text-right ml-4">
          <div className="text-2xl font-bold text-green-600 mb-1">{formatCurrency(payment.amount)}</div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.payment_status)}`}>
            {payment.payment_status}
          </span>
        </div>
      </div>

      {/* Payment Details */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Amount</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
          </div>
          {payment.service_fee > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Service Fee</p>
              <p className="text-sm font-medium text-blue-600">{formatCurrency(payment.service_fee)}</p>
            </div>
          )}
          {payment.driver_earnings > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Driver Earnings</p>
              <p className="text-sm font-medium text-purple-600">{formatCurrency(payment.driver_earnings)}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Transaction ID</p>
            <p className="text-xs font-mono text-gray-600 bg-white px-2 py-1 rounded border">
              {payment.transaction_id.slice(-8)}
            </p>
          </div>
        </div>
      </div>

      {/* Driver/Customer Info */}
      {payment.driver_name && (
        <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-blue-800">
                {payment.driver_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{payment.driver_name}</p>
              <p className="text-xs text-gray-600">Service Provider</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          {onViewDetails && (
            <button
              onClick={onViewDetails}
              className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium hover:bg-blue-50 rounded-lg transition-colors"
            >
              View Details
            </button>
          )}
          {payment.payment_status === 'completed' && onDownloadReceipt && (
            <button
              onClick={onDownloadReceipt}
              className="px-4 py-2 text-green-600 hover:text-green-800 text-sm font-medium hover:bg-green-50 rounded-lg transition-colors"
            >
              📄 Receipt
            </button>
          )}
        </div>

        {/* Status Timeline */}
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <div className={`w-2 h-2 rounded-full ${payment.payment_status === 'completed' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
          <span>Processed</span>
          {payment.completed_at && (
            <>
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span>Completed</span>
            </>
          )}
        </div>
      </div>

      {/* Security Badge */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
          <span>🔒</span>
          <span>Secured by SSL • Transaction verified</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentCard;
